import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  IProjectConfigurationResponseData,
  IProjectCategoriesResponseData,
  ITemplateFoldersResponseData,
  ICreateTemplateFolderRequest,
  ICreateTemplateFolderResponseData,
  ITemplatesResponseData,
  ITemplate,
  ISelectItemObjectData,
  IStudioCategory,
  IStudioStage,
} from "./uiTypes";
import {
  getCategoriesAPI,
  getCategoryFolderAPI,
  getProjectAPI,
  getProjectStagesAPI,
  getProductsAPI,
  IProductsResponseData,
  searchForProduct,
} from "../../../processes/api/project";
import { ISearchProjectsResponseData } from "./uiTypes";

import {
  getTemplatesPaginatedAPI,
  createTemplateAPI,
  deleteTemplateAPI,
  updateTemplateAPI,
  getTemplateAPI,
  overwriteTemplateAPI,
} from "../../../processes/api/templates";

import { getCurrentAdminStageAPI } from "../../../processes/api/stage";
import { RootState } from "../../store";
import {
  getTemplateFoldersPaginatedAPI,
  createTemplateFolderAPI,
  updateTemplateFolderAPI,
  deleteTemplateFolderAPI,
} from "../../../processes/api/templateFolders";
import { getConfigInfo } from "../../../utils/functions/playcanvas/getConfigInfo";
import { resetScene } from "../../../utils/functions/playcanvas/resetScene";
import { loadConfiguration } from "../../../utils/functions/playcanvas/loadConfiguration";
import { setCameraPosition } from "../../../utils/functions/playcanvas/setCameraPosition";
import { setCameraFOV } from "../../../utils/functions/playcanvas/setCameraFOV";
import { studioAxiosInstance } from "../../../processes/api/instances";
import PlayCanvasService from "../../../services/PlayCanvasService/PlayCanvasService";
import {
  setDataUI,
  setCameraTransform,
  updateCameraSetting,
  updateCameraSettingEnabled,
  setAspectRatio,
} from "./uiReducer";
import { getPathToRoot, getProductFromCache } from "./selectors/objectSelectors";
import {
  getIsSearching,
  getSearchProjectPathsByProjectId,
} from "./selectors/searchSelectors";
import { UI_ACTION_NAME } from "../types";
import { resolutionList, type resolutionListT } from "../../../utils/constants";
import { ScreenDimensionsService } from "../../../services/ScreenDimensionsService";

const { REACT_APP_NODE_ENV_KEY } = process.env;

// Projects

export const getProjectThunk = createAsyncThunk<
  IProjectConfigurationResponseData,
  number
>("ui/getStagesListThunk:fetch", async organizationId => {
  const response = await getProjectAPI(organizationId);

  return response.data;
});

export const getProjectStagesThunk = createAsyncThunk<IStudioStage, number>(
  "ui/getProjectStagesThunk:fetch",
  async stageId => {
    // Defensive: avoid calling API with NaN/invalid ids
    if (!Number.isFinite(stageId)) {
      throw new Error("Invalid stageId: not a finite number");
    }
    console.log("stageId After refreshing", stageId);
    const response = await getProjectStagesAPI(stageId);

    return response.data;
  }
);

export const getCategoriesThunk = createAsyncThunk<
  IProjectCategoriesResponseData,
  number
>("ui/getCategoriesThunk:fetch", async organizationId => {
  const response = await getCategoriesAPI(organizationId);

  return response.data;
});

export const getCategoryInfoThunk = createAsyncThunk<IStudioCategory, number>(
  "ui/getCategoryInfoThunk:fetch",
  async categoryId => {
    const response = await getCategoryFolderAPI(categoryId);

    return response.data;
  }
);

export const getProductsThunk = createAsyncThunk<IProductsResponseData, number>(
  "ui/getProductsThunk:fetch",
  async projectId => {
    const response = await getProductsAPI(projectId);

    return response.data;
  }
);

// Search products globally by title
export const searchProductsThunk = createAsyncThunk<
  ISearchProjectsResponseData,
  string
>("ui/searchProductsThunk:fetch", async title => {
  const response = await searchForProduct(title);
  return response.data;
});

// Projects

// Template Folders

export const fetchTemplateFoldersThunk = createAsyncThunk<
  ITemplateFoldersResponseData,
  { page?: number }
>("ui/fetchTemplateFoldersThunk:fetch", async ({ page = 1 }, { getState }) => {
  const state = getState() as RootState;
  const { pageSize } = state.app;

  const response = await getTemplateFoldersPaginatedAPI({
    page,
    pageSize,
  });

  return response.data;
});

export const createTemplateFolderThunk = createAsyncThunk<
  ICreateTemplateFolderResponseData,
  ICreateTemplateFolderRequest
>("ui/createTemplateFolderThunk:create", async (data, { dispatch }) => {
  const response = await createTemplateFolderAPI(data);

  // Re-fetch the folders after creation
  await dispatch(fetchTemplateFoldersThunk({}));

  return response.data;
});

export const updateTemplateFolderThunk = createAsyncThunk<
  ICreateTemplateFolderResponseData,
  { folderId: number; data: ICreateTemplateFolderRequest }
>(
  "ui/updateTemplateFolderThunk:update",
  async ({ folderId, data }, { dispatch }) => {
    const response = await updateTemplateFolderAPI(folderId, data);

    await dispatch(fetchTemplateFoldersThunk({}));

    return response.data;
  }
);

export const deleteTemplateFolderThunk = createAsyncThunk<void, number>(
  "ui/deleteTemplateFolderThunk:delete",
  async (folderId, { dispatch }) => {
    await deleteTemplateFolderAPI(folderId);

    await dispatch(fetchTemplateFoldersThunk({}));
  }
);

// Template Folders

// Stages

export const getCurrentStageThunk = createAsyncThunk<any, { stageId: number }>(
  "ui/getCurrentStageThunk:fetch",
  async ({ stageId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    // const { organizationId } = state;

    const response = await getCurrentAdminStageAPI({
      organizationId: REACT_APP_NODE_ENV_KEY === "production" ? 5 : 2,
      stageId,
    });

    console.log("REACT_APP_NODE_ENV_KEY");
    console.log("REACT_APP_NODE_ENV_KEY");
    console.log("REACT_APP_NODE_ENV_KEY");
    console.log(response);

    return response.data;
  }
);

// Stages

// Templates
export const fetchTemplatesThunk = createAsyncThunk<
  ITemplatesResponseData,
  { folderId: number; page?: number }
>(
  "ui/fetchTemplatesThunk:fetch",
  async ({ folderId, page = 1 }, { getState }) => {
    const state = getState() as RootState;
    const { pageSize } = state.app;

    const response = await getTemplatesPaginatedAPI({
      folderId,
      page,
      pageSize,
    });
    return response.data;
  }
);

// Helper to assemble FormData for template creation/update
function assembleTemplateFormData(
  { name, folderId, config }: { name: string; folderId?: number; config: any },
  imageFile?: File
): FormData {
  const formData = new FormData();
  formData.append("name", name);
  if (folderId !== undefined) {
    formData.append("folderId", String(folderId));
  }
  // Ensure config is stringified only once
  const configString =
    typeof config === "string" ? config : JSON.stringify(config);
  formData.append("config", configString);
  if (imageFile) {
    formData.append("image", imageFile);
  }
  return formData;
}

// Helper to build flat config for PlayCanvas LoadConfig
function prepareFlatConfig(sceneConfig: any) {
  return {
    stage: sceneConfig.stage,
    camera: {
      position: sceneConfig.camera.transform.position,
      rotation: {
        x: sceneConfig.camera.transform.rotation.pitch,
        y: sceneConfig.camera.transform.rotation.yaw,
        z: sceneConfig.camera.transform.rotation.roll,
      },
      scale: sceneConfig.camera.transform.scale,
      fov: sceneConfig.camera.fov,
      focalDistance: sceneConfig.camera.focalDistance,
      sensorWidth: sceneConfig.camera.sensorWidth,
      exposure: sceneConfig.camera.exposure,
      forward: sceneConfig.camera.forward,
      up: sceneConfig.camera.up,
      imageWidth: sceneConfig.camera.imageWidth,
      imageHeight: sceneConfig.camera.imageHeight,
      renderWidth: sceneConfig.camera.renderWidth,
      renderHeight: sceneConfig.camera.renderHeight,
      antiAliasing: sceneConfig.camera.antiAliasing,
    },
    products: sceneConfig.products.map((p: any) => {
      // Handle scale - convert number to Vec3 if needed
      const scaleValue = p.scale;
      const scaleVec3 = typeof scaleValue === "number"
        ? { x: scaleValue, y: scaleValue, z: scaleValue }
        : (scaleValue || { x: 1, y: 1, z: 1 });

      const glbBundleUrl = p.glbResourceUrl || p.glbBundle || p.resourceUrl;
      console.log(`DEBUG prepareFlatConfig: product ${p.id}, glbResourceUrl=${p.glbResourceUrl}, glbBundle=${p.glbBundle}, resourceUrl=${p.resourceUrl}, final glbBundle=${glbBundleUrl}`);

      return {
        id: p.id,
        glbBundle: glbBundleUrl,
        resourceUrl: p.resourceUrl,
        transform: {
          position: p.position,
          rotation: p.rotation,
          scale: scaleVec3,
        },
        // pathObjectIds is not needed for PlayCanvas config, only for Redux state
      };
    }),
  };
}

// Helper to refresh templates list based on a template ID
async function refreshTemplatesFor(
  templateId: number,
  dispatch: any,
  getState: () => any
): Promise<void> {
  const state = getState() as RootState;
  const template = state.ui.templates.find(t => t.id === templateId);
  const folderId = template?.folderId;
  if (folderId != null) {
    await dispatch(fetchTemplatesThunk({ folderId }));
  }
}

// Delete Template
export const deleteTemplateThunk = createAsyncThunk<void, number>(
  "ui/deleteTemplateThunk:delete",
  async (templateId, { dispatch, getState }) => {
    // Call API to delete the template
    await deleteTemplateAPI(templateId);
    // Refresh templates in the folder
    await refreshTemplatesFor(templateId, dispatch, getState);
  }
);

// Restore Template
export const restoreTemplateThunk = createAsyncThunk<ITemplate, number>(
  "ui/restoreTemplateThunk:restore",
  async (templateId, { dispatch, getState }) => {
    
    const response = await getTemplateAPI(templateId);
    const template = response.data;

    if (!template.config) {
      throw new Error("Template configuration is missing");
    }

    await resetScene();

    // Load the template configuration
    const parsed =
      typeof template.config === "string"
        ? JSON.parse(template.config)
        : template.config;
    const sceneConfig = parsed.config || parsed;

    // Debug: Log first product to see its structure
    if (sceneConfig.products && sceneConfig.products.length > 0) {
      console.log("DEBUG: First product in template:", JSON.stringify(sceneConfig.products[0], null, 2));
    }

    const flatConfig = prepareFlatConfig(sceneConfig);

    await loadConfiguration(JSON.stringify(flatConfig));

    // Prefetch products for all involved projects so options are available from cache
    try {
      const uniqueProjectIds: number[] = Array.from(
        new Set(
          (sceneConfig.products || [])
            .map((p: any) => Number(p.projectId))
            .filter((id: number) => Number.isFinite(id))
        )
      ) as number[];

      for (const pid of uniqueProjectIds) {
        await dispatch(getProductsThunk(pid)).unwrap();
      }
    } catch (e) {
      console.warn("Failed to prefetch products for restored template", e);
    }

    // Switch to the correct stage/environment if specified in template
    if (sceneConfig.stage && sceneConfig.stage.id) {
      try {
        console.log("Switching to template stage:", sceneConfig.stage.id);
        // Store camera settings before stage change to preserve them
        const cameraSettingsToPreserve = {
          position: sceneConfig.camera.transform?.position,
          rotation: sceneConfig.camera.transform?.rotation,
          fov: sceneConfig.camera.fov,
        };

        // Dispatch the action to trigger the middleware handler for stage switching
        dispatch({
          type: UI_ACTION_NAME.SET_ACTIVE_ENV_INDEX,
          payload: sceneConfig.stage.id,
        });
        console.log("Successfully dispatched stage switch action");

        // Wait for the stage change to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Immediately re-apply camera settings from template after stage change
        // This prevents the default camera values from being applied
        if (
          cameraSettingsToPreserve.position &&
          cameraSettingsToPreserve.rotation
        ) {
          console.log(
            "Preserving template camera position:",
            cameraSettingsToPreserve.position
          );
          await setCameraPosition({
            json: {
              pos: cameraSettingsToPreserve.position,
              rot: {
                x: cameraSettingsToPreserve.rotation.pitch,
                y: cameraSettingsToPreserve.rotation.yaw,
                z: cameraSettingsToPreserve.rotation.roll,
              },
            },
          });
          console.log("Template camera position preserved successfully");
        }

        if (cameraSettingsToPreserve.fov !== undefined) {
          console.log(
            "Preserving template camera FOV:",
            cameraSettingsToPreserve.fov
          );
          await setCameraFOV(cameraSettingsToPreserve.fov);
          console.log("Template camera FOV preserved successfully");
        }
      } catch (error) {
        console.warn("Failed to switch to template stage:", error);
        // Continue with template restoration even if stage switching fails
      }
    }

    // Update Redux camera state with template values
    if (sceneConfig.camera) {
      // Update camera transform
      if (sceneConfig.camera.transform) {
        dispatch(
          setCameraTransform({
            position: sceneConfig.camera.transform.position,
            rotation: sceneConfig.camera.transform.rotation,
            scale: sceneConfig.camera.transform.scale || { x: 1, y: 1, z: 1 },
          })
        );
      }

      // Update camera settings
      if (sceneConfig.camera.fov !== undefined) {
        const fovType = (sceneConfig.camera as any)?.fovType;
        // Only write FOV from template into Redux when it's explicitly a UI value.
        // For old templates (no fovType or non-'ui'), keep current UI FOV intact.
        if (fovType === "ui") {
          dispatch(
            updateCameraSetting({ key: "fov", value: sceneConfig.camera.fov })
          );
        }
      }
      if (sceneConfig.camera.focalDistance !== undefined) {
        dispatch(
          updateCameraSetting({
            key: "focalDistance",
            value: sceneConfig.camera.focalDistance,
          })
        );
        // Enable focal distance if it has a value
        if (sceneConfig.camera.focalDistance !== null) {
          dispatch(
            updateCameraSettingEnabled({ key: "focalDistance", enabled: true })
          );
        }
      }
      if (sceneConfig.camera.sensorWidth !== undefined) {
        dispatch(
          updateCameraSetting({
            key: "sensorWidth",
            value: sceneConfig.camera.sensorWidth,
          })
        );
        // Enable sensor width if it has a value
        if (sceneConfig.camera.sensorWidth !== null) {
          dispatch(
            updateCameraSettingEnabled({ key: "sensorWidth", enabled: true })
          );
        }
      }
      if (sceneConfig.camera.exposure !== undefined) {
        dispatch(
          updateCameraSetting({
            key: "exposure",
            value: sceneConfig.camera.exposure,
          })
        );
        // Enable exposure if it has a value
        if (sceneConfig.camera.exposure !== null) {
          dispatch(
            updateCameraSettingEnabled({ key: "exposure", enabled: true })
          );
        }
      }
    }

    // Sync UI AspectRatio from template config if present; fallback to camera dimensions
    try {
      const cfg = sceneConfig as any;
      if (cfg.aspectRatio) {
        const saved: resolutionListT = cfg.aspectRatio;
        if (typeof saved?.id === "number" && saved.id >= 0) {
          const preset = resolutionList.find(r => r.id === saved.id);
          if (preset) {
            dispatch(setAspectRatio(preset));
          } else {
            dispatch(setAspectRatio(saved));
          }
        } else if (saved?.value?.width && saved?.value?.height) {
          dispatch(
            setAspectRatio({
              value: { width: saved.value.width, height: saved.value.height },
              label: `${saved.value.width}x${saved.value.height}`,
              id: -1,
            })
          );
        }
      } else {
        const imgW: number | undefined = sceneConfig.camera?.imageWidth;
        const imgH: number | undefined = sceneConfig.camera?.imageHeight;
        const renW: number | undefined = sceneConfig.camera?.renderWidth;
        const renH: number | undefined = sceneConfig.camera?.renderHeight;

        const width = (typeof imgW === "number" && imgW > 0 ? imgW : renW) ?? 0;
        const height =
          (typeof imgH === "number" && imgH > 0 ? imgH : renH) ?? 0;

        if (width > 0 && height > 0) {
          const svc = new ScreenDimensionsService();
          const targetRatio = width / height;
          const closest = svc.findClosestAspectRatio(width, height);
          const cw = Number(closest.value?.width ?? 0);
          const ch = Number(closest.value?.height ?? 0);
          const closestRatio = ch !== 0 ? cw / ch : targetRatio;
          const diff = Math.abs(targetRatio - closestRatio);
          const TOL = 0.02;

          if (diff <= TOL) {
            dispatch(setAspectRatio(closest));
          } else {
            const custom: resolutionListT = {
              value: { width, height },
              label: `${width}x${height}`,
              id: -1,
            };
            dispatch(setAspectRatio(custom));
          }
        }
      }
    } catch (e) {
      console.warn("Failed to set aspect ratio from template", e);
    }

    // If exact frame dimensions were saved (custom aspect), broadcast them for precise restore
    try {
      const frameMeta: any = (sceneConfig as any)?.frameMeta;
      if (
        frameMeta &&
        typeof frameMeta.width === "number" &&
        typeof frameMeta.height === "number" &&
        frameMeta.width > 0 &&
        frameMeta.height > 0
      ) {
        window.dispatchEvent(
          new CustomEvent("apply-frame-meta", { detail: frameMeta })
        );
      }
    } catch {}

    // restore Redux selectObjects & activeObjectId via SET_DATA_UI ---
    const state = getState() as RootState;
    const selectObjects: Record<string, ISelectItemObjectData> = {};
    let firstUiId: string | null = null;

    sceneConfig.products.forEach((p: any) => {
      // Use saved pathObjectIds from template data instead of recomputing from UI state
      let pathObjectIds: number[];

      if (p.pathObjectIds && Array.isArray(p.pathObjectIds)) {
        // Use saved pathObjectIds from template data
        pathObjectIds = p.pathObjectIds;
      } else {
        // compute path from current UI state
        pathObjectIds = getPathToRoot(state).map(obj => obj.id);
        if (getIsSearching(state)) {
          pathObjectIds = getSearchProjectPathsByProjectId(p.projectId)(state);
        }
      }

      // unique UI id for this product instance
      const uiId = `${p.id}_${p.projectId}_${pathObjectIds.join("#")}_${Math.random()
        .toString(36)
        .substring(2, 15)}`;
      if (!firstUiId) firstUiId = uiId;

      // split template options into geometry/material selections
      const geometryOptions: Record<number, Record<number, number>> = {};
      const materialOptions: Record<number, Record<number, number>> = {};
      (p.options || []).forEach((opt: any) => {
        const target =
          opt.type === "geometry"
            ? geometryOptions
            : opt.type === "material"
              ? materialOptions
              : null;
        if (target) {
          if (!target[opt.proxyOptionId]) {
            target[opt.proxyOptionId] = {};
          }
          target[opt.proxyOptionId][opt.optionId] = opt.val;
        }
      });

      // Normalize: keep a single option family per proxy (drop extras)
      const normalizeToSingle = (
        map: Record<number, Record<number, number>>
      ) => {
        Object.keys(map).forEach(proxyIdStr => {
          const proxyId = Number(proxyIdStr);
          const optionIds = Object.keys(map[proxyId]);
          if (optionIds.length > 1) {
            const keep = optionIds[0];
            const val = map[proxyId][Number(keep)];
            map[proxyId] = { [Number(keep)]: val } as Record<number, number>;
          }
        });
      };
      normalizeToSingle(geometryOptions);
      normalizeToSingle(materialOptions);

      // Try to get GLB URL from different sources for backwards compatibility
      // Priority: glbResourceUrl (new) -> glbBundle -> resourceUrl (old templates stored GLB here)
      let glbUrl = p.glbResourceUrl ?? (p as any).glbBundle ?? p.resourceUrl;

      // If still no URL, try to get it from products cache
      if (!glbUrl) {
        const productFromCache = getProductFromCache(p.projectId, p.id)(state);
        if (productFromCache?.glbBundle) {
          glbUrl = productFromCache.glbBundle;
          console.log(
            "test RestoreTemplate: loaded GLB URL from products cache for product",
            p.id,
            glbUrl
          );
        }
      }

      if (!p.glbResourceUrl && p.resourceUrl) {
        console.log(
          "test RestoreTemplate: using resourceUrl (old template format) as GLB for product",
          p.id,
          p.resourceUrl
        );
      } else if (!p.glbResourceUrl && (p as any).glbBundle) {
        console.log(
          "test RestoreTemplate: using glbBundle URL for product",
          p.id,
          (p as any).glbBundle
        );
      }

      if (!glbUrl) {
        console.warn(
          "test RestoreTemplate: missing GLB URL for product",
          p.id,
          "- cannot find in template or products cache"
        );
      }
      // detect old vs new transform format, normalize numeric scale into vector
      // NOTE: position/rotation/scale are now LOCAL coordinates (saved from localPosition/localRotation/localScale)
      let transform;
      if ((p as any).transform) {
        transform = (p as any).transform;
      } else {
        const rawScale = (p as any).scale;
        const scaleVec =
          typeof rawScale === "number"
            ? { x: rawScale, y: rawScale, z: rawScale }
            : (rawScale ?? { x: 1, y: 1, z: 1 });
        console.log(
          "test RestoreTemplate: fallback to position/rotation/scale for product",
          p.id
        );
        transform = {
          position: p.position,  // Local position
          rotation: p.rotation,  // Local rotation
          scale: scaleVec,       // Local scale
        };
      }

      selectObjects[uiId] = {
        projectId: p.projectId,
        realId: p.id,
        pathObjectIds,
        geometryOptions,
        materialOptions,
        glbData: { url: glbUrl, transform },
      };
      console.log(
        "test RestoreTemplate: selectObjects[" + uiId + "]",
        selectObjects[uiId]
      );
    });

    // dispatch SET_DATA_UI to sync Redux and scene
    const ui = state.ui;
    dispatch(
      setDataUI({
        activeEnvironmentIndex: ui.activeEnvironmentId,
        environmentActiveValues: ui.environmentActiveValues,
        activeObjectIndex: firstUiId,
        selectedModel: ui.selectedModel,
        selectedType: ui.selectedType,
        activeTab: ui.activeTab,
        selectedVariants: ui.selectedVariants,
        selectObjects,
      })
    );

    

    try {
      localStorage.removeItem("camera-presets-local");
      const presetsResp = await studioAxiosInstance.get<any[]>(
        "cameraPresets",
        { params: { templateId } }
      );
      (template as any).cameraPresets = presetsResp.data;
    } catch (e) {
      console.log(
        "Failed to load server camera presets for restored template",
        e
      );
    }

    return template;
  }
);

// Overwrite Template
export interface IOverwriteTemplateFormRequest {
  id: number;
  config: any; // object or pre-stringified JSON
}

export const overwriteTemplateThunk = createAsyncThunk<
  void,
  IOverwriteTemplateFormRequest
>(
  "ui/overwriteTemplateThunk:update",
  async ({ id, config }, { dispatch, getState }) => {
    try {
      const raw = await getConfigInfo();
      const arr = JSON.parse(raw) as any[];
      const cameraInfo = arr.find(item => item.id === "camera");
      if (cameraInfo) {
        config.config.camera.transform = {
          ...config.config.camera.transform,
          position: cameraInfo.pos,
          rotation: {
            yaw: cameraInfo.rot.y,
            pitch: cameraInfo.rot.x,
            roll: cameraInfo.rot.z,
          },
        };
      }
    } catch (e) {
      console.log(
        "Failed to override camera transform in overwriteTemplateThunk",
        e
      );
    }

    try {
      // Step 1: Update config via /overwrite endpoint (JSON only)
      await overwriteTemplateAPI(id, { config });

      // Step 2: Update image via regular update endpoint if screenshot is available
      const screenshotFile = PlayCanvasService.captureScreenshotFile();

      if (screenshotFile) {
        // Get current template to preserve the name and use updateTemplateAPI
        const state = getState() as RootState;
        const template = state.ui.templates.find(t => t.id === id);

        if (template && template.name) {
          // Use updateTemplateAPI which properly formats the request
          const imageFormData = assembleTemplateFormData(
            { name: template.name, config },
            screenshotFile
          );
          await updateTemplateAPI(id, imageFormData);
        }
      }
    } catch (error: any) {
      console.error("Failed to overwrite template:", error);
      throw error;
    }

    await refreshTemplatesFor(id, dispatch, getState);
  }
);

// Payload for updating a template (with optional image)
export interface IUpdateTemplateFormRequest {
  id: number;
  name: string;
  config: any;
  imageFile?: File;
}
// Update Template
export const updateTemplateThunk = createAsyncThunk<
  ITemplate,
  IUpdateTemplateFormRequest
>(
  "ui/updateTemplateThunk:update",
  async ({ id, name, config, imageFile }, { dispatch, getState }) => {
    const formData = assembleTemplateFormData({ name, config }, imageFile);
    const response = await updateTemplateAPI(id, formData);
    // Refresh templates in the folder
    await refreshTemplatesFor(id, dispatch, getState);
    return response.data;
  }
);

// Payload for creating template with image upload
export interface ICreateTemplateFormRequest {
  name: string;
  folderId: number;
  config: any;
  imageFile: File;
}
// Create Template
export const createTemplateThunk = createAsyncThunk<
  ITemplate,
  ICreateTemplateFormRequest
>(
  "ui/createTemplateThunk:create",
  async ({ name, folderId, config, imageFile }, { dispatch, getState }) => {
    // Override nested camera transform using live scene info
    try {
      const raw = await getConfigInfo();
      const arr = JSON.parse(raw) as any[];
      const cameraInfo = arr.find(item => item.id === "camera");
      if (cameraInfo) {
        config.config.camera.transform = {
          ...config.config.camera.transform,
          position: cameraInfo.pos,
          rotation: {
            yaw: cameraInfo.rot.y,
            pitch: cameraInfo.rot.x,
            roll: cameraInfo.rot.z,
          },
        };
      }
    } catch (e) {
      console.log(
        "Failed to override camera transform in createTemplateThunk",
        e
      );
    }
    const formData = assembleTemplateFormData(
      { name, folderId, config },
      imageFile
    );
    const response = await createTemplateAPI(formData);

    // Sync local camera presets to server
    try {
      const stored = localStorage.getItem("camera-presets-local");
      const localPresets = stored ? JSON.parse(stored) : [];
      const templateId = response.data.id;

      // Gather live camera transform data including rotation
      const configString = await getConfigInfo();
      const configArray = JSON.parse(configString);
      const cameraInfo = configArray.find((item: any) => item.id === "camera");
      if (cameraInfo) {
        // Save preset without rendering
        for (const preset of localPresets) {
          const payload = {
            templateId,
            camera: preset.config,
            name: preset.label,
          };
          await studioAxiosInstance.post<any>("cameraPresets", payload, {
            params: { templateId },
          });
        }
        // Clear local presets now that they've been synced to server
        localStorage.removeItem("camera-presets-local");
      }
    } catch (e) {
      console.log("Failed to sync local camera presets to server", e);
    }

    // Re-fetch templates after creation
    await dispatch(fetchTemplatesThunk({ folderId }));
    return response.data;
  }
);

// Templates
